import DownloadIcon from '@/assets/icons/DownloadIcon'
import EditIcon from '@/assets/icons/EditIcon'
import CloseIcon from '@/assets/icons/MTCancelIcon'
import SaveIcon from '@/assets/icons/MTSaveIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import CustomButton from '@/components/Returns/CustomButton'
import { ConfirmationDialog } from '@/components/common'
import Search from '@/components/common/Search'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { useDefineDataType } from '@/hooks/useDefineDataType'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import {
  bulkUpdateServiceMasterRecords,
  createServiceMasterRecord,
  deleteDtaaMasterRecord,
  downloadServiceMaster,
  fetchServiceMasterList,
  fetchServiceMasterMetadata,
  metadataToFormFields,
  metadataToTableColumns,
  triggerBlobDownload,
  updateServiceMasterRecord,
} from '@/multi-territory/mt-services/mt-service-master-api'
import type {
  MTMetadataFormField,
  MTMetadataTableColumn,
  MTServiceMasterRecord,
} from '@/multi-territory/mt-types/mt-metadata'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { MTServiceMasterForm } from './MTServiceMasterForm'
import { setMTNavigationGuard } from '@/multi-territory/mt-utils/mt-navigation-guard'
const ServiceMasterSearch = Search as any

// MTWHT-CR-01 messages
const MSG_NO_CHANGES = 'No changes detected to update.'
const MSG_UPDATE_SUCCESS = 'Section Master updated successfully.'
const MSG_UNSAVED_CHANGES =
  'You have unsaved changes. Navigating away will discard your edits. Do you want to proceed?'

/**
 * MTServiceMasterTable - Metadata-driven Service Master table with full API integration.
 *
 * APIs integrated:
 * - POST /service-master/list (paginated list with search)
 * - POST /service-master (create)
 * - PUT /service-master/:id (update)
 * - POST /service-master/download (Excel export)
 * - GET /data/{countryCode}/SERVICE_MASTER/metadata (column/form config)
 */
export function MTServiceMasterTable() {
  const [columns, setColumns] = useState<MTMetadataTableColumn[]>([])
  const [formFields, setFormFields] = useState<MTMetadataFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('Add nature of service')
  const [data, setData] = useState<MTServiceMasterRecord[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE || 10
  )
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [editMode, setEditMode] = useState(false)
  // true when Edit was clicked with no selection -> every row on the page is editable
  const [isEditAll, setIsEditAll] = useState(false)
  // MTWHT-CR-01: selection-driven edit. Only rows in `selectedItems` are
  // editable while editMode is on. Keyed by row uuid: { [uuid]: { field: value } }
  const [editingData, setEditingData] = useState<
    Record<string, Record<string, any>>
  >({})
  // rowId -> fieldKey -> true, highlights invalid cells after Save
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, Record<string, boolean>>
  >({})
  // Action deferred behind the "unsaved changes" confirmation dialog
  const [pendingNavAction, setPendingNavAction] = useState<(() => void) | null>(
    null
  )
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const searchIconRef = useRef<HTMLButtonElement>(null)
  const [searchCOList, setSearchCOList] = useState<any[]>([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: '',
      value: '',
    },
  ])
  const [downloading, setDownloading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [showTable, setShowTable] = useState(false)
  const [rawMetadataFields, setRawMetadataFields] = useState<any[]>([])
  const [allData, setAllData] = useState<MTServiceMasterRecord[]>([])
  const primaryFields = useMemo(
    () => (rawMetadataFields || []).filter(f => f.validations?.primary).map(f => f.field_key),
    [rawMetadataFields]
  )

  // DTAA Edit/Delete state
  const [gapWarningOpen, setGapWarningOpen] = useState(false)
  const [pendingBulkPayload, setPendingBulkPayload] = useState<Array<{
    uuid: string
    [key: string]: any
  }> | null>(null)

  const parseRowDate = (val: any): Date | null => {
    if (!val) return null
    const s = String(val)
    if (s.includes('/')) {
      const parts = s.split('/')
      if (parts.length === 3)
        return new Date(Date.UTC(+parts[2], +parts[1] - 1, +parts[0]))
    }
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch)
      return new Date(Date.UTC(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]))
    return null
  }

  const checkInlineGap = (
    editingUuid: string,
    mergedData: Record<string, any>
  ): boolean => {
    const sourceData = allData.length ? allData : data
    if (!primaryFields.length || !sourceData.length) return false

    const newStartDate = parseRowDate(mergedData['start_date'])
    if (!newStartDate) return false

    const editingRow = sourceData.find(r => r.uuid === editingUuid)
    const editingUuids = new Set(
      [
        editingUuid,
        editingRow?.associated_uuid,
        editingRow?.global_uuid,
        editingRow?.uuid,
        editingRow?.id,
      ].filter(Boolean)
    )

    const sameGroup = sourceData.filter(row => {
      const rowUuids = [
        row.associated_uuid,
        row.global_uuid,
        row.uuid,
        row.id,
      ].filter(Boolean)
      if (rowUuids.some(id => editingUuids.has(id))) return false
      return primaryFields.every(
        key => String(row[key] ?? '') === String(mergedData[key] ?? '')
      )
    })

    // Only check against the LATEST end_date in the group
    const latestEndDate = sameGroup
      .map(row => parseRowDate(row.end_date))
      .filter((d): d is Date => d !== null)
      .reduce<Date | null>(
        (max, d) => (max === null || d.getTime() > max.getTime() ? d : max),
        null
      )

    if (!latestEndDate) return false
    const dayAfterLatestEndMs = latestEndDate.getTime() + 24 * 60 * 60 * 1000
    return newStartDate.getTime() > dayAfterLatestEndMs
  }
  const [dtaaEditOpen, setDtaaEditOpen] = useState(false)
  const [dtaaEditingRow, setDtaaEditingRow] =
    useState<MTServiceMasterRecord | null>(null)
  const [dtaaDeleteOpen, setDtaaDeleteOpen] = useState(false)
  const [dtaaDeleteUuid, setDtaaDeleteUuid] = useState<string | null>(null)
  const [dtaaDeleteCountryName, setDtaaDeleteCountryName] = useState<string>('')

  const { showSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  // Entity metadata to determine tanApplicable
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true

  /**
   * API payload logic:
   * - tanApplicable=true  -> send tanMasterUuid (selected TAN), entityMasterUuid = null
   * - tanApplicable=false -> send entityMasterUuid (selected entity), tanMasterUuid = null
   *
   * The `fetchServiceMasterList` uses the second param as X-Entity-Master-Id header.
   * We pass the correct identifier based on tanApplicable.
   */
  const apiIdentifierUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid
  const lastFetchedKeyRef = useRef('')

  // Derive dataType from navigation API instead of hardcoding
  const { dataType: navDataType, subModules: navSubModules } =
    useDefineDataType('SERVICE_MASTER')

  // Tab 0 = first sub-module (NATURE_OF_SERVICE) -> metadata uses parent code SERVICE_MASTER
  // Tab 1 = second sub-module (DTAA_MASTER) -> metadata uses DTAA_MASTER
  const activeDataType = useMemo(() => {
    if (activeTab > 0 && activeTab < navSubModules.length) {
      return navSubModules[activeTab].code
    }
    // Tab 0 or single sub-module: use the parent code for metadata
    return navDataType || 'SERVICE_MASTER'
  }, [activeTab, navSubModules, navDataType])
  const activeDataTypeLower = activeDataType.toLowerCase().replace(/_/g, '-')

  const loadMetadata = useCallback(async () => {
    if (!countryCode) return
    const metaResult = await fetchServiceMasterMetadata(
      countryCode,
      activeDataType
    )
    if (metaResult.success) {
      if (metaResult.data.form_title) setFormTitle(metaResult.data.form_title)
      setColumns(metadataToTableColumns(metaResult.data.fields))
      setFormFields(metadataToFormFields(metaResult.data.fields))
      setRawMetadataFields(metaResult.data.fields || [])
    }
  }, [countryCode, activeDataType])
  useEffect(() => {
    loadMetadata()
  }, [loadMetadata])

  const loadAllDataForGapCheck = useCallback(async () => {
    if (!countryCode || !apiIdentifierUuid) return
    const payload = {
      pageFilter: {
        currentPage: 1,
        pageSize: 10000,
        sortField: 'lastModifiedDate',
        sortOrder: 'DESC' as const,
      },
      searchCOList: [],
    }
    const result = await fetchServiceMasterList(
      countryCode,
      apiIdentifierUuid,
      payload,
      activeDataTypeLower,
      { tanApplicable }
    )
    if (result.success) {
      setAllData(
        result.data.content.map((row, idx) => ({
          ...row,
          uuid:
            row.associated_uuid ||
            row.global_uuid ||
            row.uuid ||
            row.id ||
            `row-${idx}`,
        }))
      )
    }
  }, [countryCode, apiIdentifierUuid, activeDataTypeLower, tanApplicable])

  const loadData = useCallback(
    async (searchList?: any[]) => {
      if (!countryCode || !apiIdentifierUuid) return
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC' as const,
        },
        searchCOList: searchList ?? searchCOList,
      }
      const dataResult = await fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        activeDataTypeLower,
        { tanApplicable }
      )
      if (dataResult.success) {
        setData(
          dataResult.data.content.map((row, idx) => ({
            ...row,
            uuid:
              row.associated_uuid ||
              row.global_uuid ||
              row.uuid ||
              row.id ||
              `row-${idx}`,
          }))
        )
        setTotalItems(dataResult.data.totalElements)
      } else {
        showSnackbar(dataResult.error, 'error')
      }
      setLoading(false)
    },
    [
      countryCode,
      apiIdentifierUuid,
      currentPage,
      itemsPerPage,
      searchCOList,
      showSnackbar,
      activeDataTypeLower,
    ]
  )

  // Load all data for gap check whenever the table is shown or data type changes
  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) {
      loadAllDataForGapCheck()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTable, activeDataTypeLower, apiIdentifierUuid, countryCode])

  // Reload list data when switching between tabs (Service Master <-> DTAA Master)
  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) {
      // Reset pagination and search state on tab switch
      setCurrentPage(1)
      setSearchCOList([])
      setSearchCriteria([
        { id: 1, logicalOperator: 'Where', field: '', value: '' },
      ])
      setSelectedItems([])
      setEditMode(false)
      setIsEditAll(false)
      setEditingData({})
      setFieldErrors({})
      lastFetchedKeyRef.current = ''
      // Fetch list data for the new data type after metadata loads
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage: 1,
          pageSize: itemsPerPage,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC' as const,
        },
        searchCOList: [],
      }
      fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        activeDataTypeLower,
        { tanApplicable }
      ).then(dataResult => {
        if (dataResult.success) {
          setData(
            dataResult.data.content.map((row: any, idx: number) => ({
              ...row,
              uuid:
                row.associated_uuid ||
                row.global_uuid ||
                row.uuid ||
                row.id ||
                `row-${idx}`,
            }))
          )
          setTotalItems(dataResult.data.totalElements)
        } else {
          showSnackbar(dataResult.error, 'error')
        }
        setLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataTypeLower])

  // Reload data when pagination changes (page or page size)
  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage])

  // Get viewTrigger from Redux (incremented when View button is clicked)
  const viewTrigger = useSelector(
    (state: any) => state.mtEntityTan?.viewTrigger ?? 0
  )

  const lastViewTriggerRef = useRef(viewTrigger)
  // Load data when View button is clicked (viewTrigger increments)
  useEffect(() => {
    if (viewTrigger > 0 && apiIdentifierUuid && countryCode) {
      if (viewTrigger <= lastViewTriggerRef.current) return
      lastViewTriggerRef.current = viewTrigger
      const fetchKey = selectedEntityUuid + '|' + selectedTanUuid
      if (showTable && lastFetchedKeyRef.current === fetchKey) return
      setShowTable(true)
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC' as const,
        },
        searchCOList,
      }
      fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        activeDataTypeLower,
        { tanApplicable }
      ).then(dataResult => {
        if (dataResult.success) {
          lastFetchedKeyRef.current = fetchKey
          setData(
            dataResult.data.content.map((row: any, idx: number) => ({
              ...row,
              uuid:
                row.associated_uuid ||
                row.global_uuid ||
                row.uuid ||
                row.id ||
                `row-${idx}`,
            }))
          )
          setTotalItems(dataResult.data.totalElements)
        } else {
          showSnackbar(dataResult.error, 'error')
        }
        setLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTrigger])

  const visibleColumns = useMemo(() => {
    const cols = columns.filter(col => !col.id.startsWith('sys_'))
    if (activeTab === 1) {
      // Add Actions column for DTAA master tab
      return [
        ...cols,
        {
          id: '_action',
          label: 'Actions',
          type: 'text' as const,
          sortable: false,
          editable: false,
          width: '60px',
          minWidth: '100px',
        },
      ]
    }
    return cols
  }, [columns, activeTab])
  const searchFields = useMemo(
    () =>
      visibleColumns
        .filter(col => col.id !== '_action')
        .map(col => {
          // Match the advance-search label to the table header column exactly
          // (sentence case / "running text", acronyms such as ID/TIN uppercase).
          const label = formatMTHeaderLabel(col.label)
          return {
            id: col.id,
            label,
            type:
              col.type === 'date'
                ? 'date'
                : col.type === 'dropdown'
                  ? 'dropdown'
                  : 'text',
            placeholder:
              col.type === 'date'
                ? 'DD/MM/YYYY'
                : col.type === 'dropdown'
                  ? `Select ${label}`
                  : `Enter ${label}`,
            ...(col.type === 'dropdown' ? { options: col.options ?? [] } : {}),
          }
        }),
    [visibleColumns]
  )

  // Set default search field once columns are loaded
  useEffect(() => {
    if (
      visibleColumns.length > 0 &&
      searchCriteria.length > 0 &&
      !searchCriteria[0].field
    ) {
      setSearchCriteria(prev =>
        prev.map((c, i) =>
          i === 0 ? { ...c, field: visibleColumns[0].id } : c
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns])

  /** Normalises a value for change comparison ('' / null / undefined / '-' are equal). */
  const normalizeValue = (v: any): string => {
    if (v === null || v === undefined) return ''
    const s = String(v).trim()
    return s === '-' ? '' : s
  }

  /**
   * Returns only the edited rows whose values actually differ from the
   * original record (edit-then-revert is NOT treated as a change).
   */
  const getChangedRows = useCallback((): Array<
    [string, Record<string, any>]
  > => {
    return Object.entries(editingData).filter(([rowUuid, changes]) => {
      const original = data.find(r => r.uuid === rowUuid)
      if (!original) return false
      return Object.entries(changes || {}).some(
        ([key, val]) => normalizeValue(val) !== normalizeValue(original[key])
      )
    })
  }, [editingData, data])

  // Any active edit session is treated as unsaved: once Edit is clicked the
  // user must Save or Cancel (or confirm discard) before filtering, paging,
  // switching tabs or navigating away.
  const hasUnsavedChanges = editMode

  /**
   * AC3 – Unsaved changes warning. Runs `action` immediately when there are
   * no unsaved edits, otherwise asks the user to confirm discarding them.
   */
  const guardUnsavedChanges = (action: () => void) => {
    if (!hasUnsavedChanges) {
      if (editMode) handleCancelEdit()
      action()
      return
    }
    setPendingNavAction(() => action)
  }
  const handleUnsavedProceed = () => {
    const action = pendingNavAction
    setPendingNavAction(null)
    handleCancelEdit()
    action?.()
  }
  const handleUnsavedCancel = () => setPendingNavAction(null)

  // Browser refresh / tab close while edits are pending
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasUnsavedChanges])

  // Navigate-away guard (HashRouter has no useBlocker):
  // 1) MT sidebar / country switcher / key-filter View route through
  //    runWithMTNavigationGuard -> this guard opens the confirmation dialog.
  // 2) In-app links (<NavLink> sub-tabs, breadcrumbs) are intercepted in the
  //    capture phase so the route does not change until the user proceeds.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const unregister = setMTNavigationGuard(proceed => {
      setPendingNavAction(() => proceed)
      return true
    })
    const onLinkClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      const href = anchor?.getAttribute('href')
      if (!anchor || !href || !href.startsWith('#/')) return
      if (anchor.target && anchor.target !== '_self') return
      const to = href.slice(1)
      if (to === window.location.hash.slice(1)) return
      e.preventDefault()
      e.stopPropagation()
      setPendingNavAction(() => () => navigate(to))
    }
    document.addEventListener('click', onLinkClick, true)
    return () => {
      unregister()
      document.removeEventListener('click', onLinkClick, true)
    }
  }, [hasUnsavedChanges, navigate])

  // India Section Master parity: selection is locked while in edit mode
  // (the editable row set is fixed when Edit is clicked).
  const handleSelectItem = (uuid: string) => {
    if (editMode) return
    setSelectedItems(p =>
      p.includes(uuid) ? p.filter(id => id !== uuid) : [...p, uuid]
    )
  }
  const handleSelectAll = () => {
    if (editMode) return
    const pageIds = data.map(r => r.uuid as string)
    const allSelected =
      pageIds.length > 0 && pageIds.every(id => selectedItems.includes(id))
    setSelectedItems(allSelected ? [] : pageIds)
  }

  /**
   * Edit icon — same as India Section Master (ActionManager.handleSectionMasterEdit):
   * - 0 rows selected  -> bulk edit: every row on the page becomes editable
   * - 1 row selected   -> single-row edit
   * - >1 rows selected -> blocked with a warning
   */
  const handleEditClick = () => {
    if (data.length === 0) {
      showSnackbar('No data available to update.', 'warning')
      return
    }
    if (selectedItems.length > 1) {
      showSnackbar(
        'Select exactly one row to edit, or deselect all to bulk update.',
        'warning'
      )
      return
    }
    const editAll = selectedItems.length === 0
    setIsEditAll(editAll)
    setEditMode(true)
    setEditingData({})
    setFieldErrors({})
    showSnackbar(
      editAll
        ? 'Edit mode enabled for all rows. Click Save to apply all changes.'
        : 'Edit mode enabled for the selected row. Click Save to apply changes.',
      'info'
    )
  }
  const handleFieldChange = (
    rowId: string,
    fieldKey: string,
    value: string
  ) => {
    setEditingData(p => ({
      ...p,
      [rowId]: { ...(p[rowId] || {}), [fieldKey]: value },
    }))
    // Clear the red border on this cell once the user edits it
    setFieldErrors(prev => {
      if (!prev[rowId]?.[fieldKey]) return prev
      const next = { ...prev, [rowId]: { ...prev[rowId] } }
      delete next[rowId][fieldKey]
      return next
    })
  }

  /**
   * Validates ALL editable fields of a row against metadata rules (AC5 –
   * existing validations). Returns every failing field so each invalid cell
   * can be highlighted, not just the first one.
   */
  const getRowValidationErrors = (
    values: Record<string, any>
  ): Array<{ fieldKey: string; message: string }> => {
    const errors: Array<{ fieldKey: string; message: string }> = []
    for (const field of rawMetadataFields) {
      if (!field.editable || !field.visible) continue
      const message = getFieldValidationError(field, values)
      if (message) errors.push({ fieldKey: field.field_key, message })
    }
    return errors
  }

  /** Validates a single metadata field. Returns an error message or null. */
  const getFieldValidationError = (
    field: any,
    values: Record<string, any>
  ): string | null => {
    const value = String(values[field.field_key] ?? '').trim()

    // Required check
    if (field.required && (!value || value === '-')) {
      return field.validations?.required_message || `${field.label} is required`
    }
    if (!value || value === '-') return null

    // Number min/max check
    if (field.type === 'number') {
      const numVal = parseFloat(value)
      if (isNaN(numVal)) return `${field.label} must be a valid number`
      if (
        field.validations?.min !== undefined &&
        numVal < field.validations.min
      ) {
        return `${field.label} must be at least ${field.validations.min}`
      }
      if (
        field.validations?.max !== undefined &&
        numVal > field.validations.max
      ) {
        return `${field.label} must not exceed ${field.validations.max}`
      }
    }

    // Max length check
    if (
      field.validations?.maxLength &&
      value.length > field.validations.maxLength
    ) {
      return `${field.label} exceeds max length ${field.validations.maxLength}`
    }

    // Regex check - skip for dropdown/date fields
    if (
      field.validations?.regex &&
      field.type !== 'dropdown' &&
      field.type !== 'date'
    ) {
      try {
        if (!new RegExp(field.validations.regex).test(value)) {
          return (
            field.validations.regex_message ||
            `${field.label} has invalid format`
          )
        }
      } catch {
        /* skip */
      }
    }
    return null
  }

  /** Builds the backend payload: only metadata-defined keys, numbers parsed. */
  const buildTypedPayload = (
    mergedData: Record<string, any>
  ): Record<string, any> => {
    const allowedKeys = new Set(rawMetadataFields.map(f => f.field_key))
    const typedData: Record<string, any> = {}
    for (const key of allowedKeys) {
      typedData[key] = mergedData[key]
    }
    for (const field of rawMetadataFields) {
      if (field.type === 'number' && typedData[field.field_key] !== undefined) {
        const raw = String(typedData[field.field_key] ?? '').trim()
        typedData[field.field_key] = raw ? parseFloat(raw) : null
      }
    }
    return typedData
  }

  const submitBulkUpdate = async (
    records: Array<{ uuid: string; [key: string]: any }>
  ) => {
    setLoading(true)
    const result = await bulkUpdateServiceMasterRecords(
      countryCode,
      apiIdentifierUuid,
      records,
      activeDataTypeLower,
      { tanApplicable }
    )
    setLoading(false)
    if (result.success) {
      // AC3 – success toast, exit edit mode, clear selection, refresh grid
      showSnackbar(MSG_UPDATE_SUCCESS, 'success')
      handleCancelEdit()
      loadData()
      loadAllDataForGapCheck()
    } else {
      // All-or-nothing on the BE: nothing was saved. Keep the user's edits so
      // they can fix and retry.
      showSnackbar(result.error || 'Failed to update records', 'error')
      loadAllDataForGapCheck()
    }
  }

  /**
   * AC1/AC3 – Save. Validates every edited row (all fields), highlights every
   * invalid cell, and saves all changed rows in one bulk call.
   */
  const handleSaveEdit = async () => {
    const changedRows = getChangedRows()
    if (changedRows.length === 0) {
      showSnackbar(MSG_NO_CHANGES, 'info')
      return
    }

    const sourceData = allData.length ? allData : data
    const records: Array<{ uuid: string; [key: string]: any }> = []
    const errors: Record<string, Record<string, boolean>> = {}
    let firstError: string | null = null
    let hasGap = false

    for (const [rowUuid, changes] of changedRows) {
      if (rowUuid.startsWith('row-')) {
        showSnackbar(
          'Cannot save: record UUID not found. Try refreshing the data.',
          'error'
        )
        return
      }
      const rowIndex = data.findIndex(r => r.uuid === rowUuid)
      const originalRow =
        data.find(r => r.uuid === rowUuid) ??
        sourceData.find(r => r.uuid === rowUuid) ??
        {}
      const mergedData: Record<string, any> = { ...originalRow, ...changes }

      const rowErrors = getRowValidationErrors(mergedData)
      if (rowErrors.length > 0) {
        errors[rowUuid] = {}
        rowErrors.forEach(e => (errors[rowUuid][e.fieldKey] = true))
        if (!firstError)
          firstError = `Row ${rowIndex + 1}: ${rowErrors[0].message}`
        continue
      }
      if (checkInlineGap(rowUuid, mergedData)) hasGap = true
      // Only metadata-defined keys are sent; list-only keys are dropped.
      records.push({ uuid: rowUuid, ...buildTypedPayload(mergedData) })
    }

    setFieldErrors(errors)
    if (firstError) {
      const invalidCount = Object.keys(errors).length
      showSnackbar(
        invalidCount > 1
          ? `${firstError} (${invalidCount} rows have errors)`
          : firstError,
        'error'
      )
      return
    }

    if (hasGap) {
      setPendingBulkPayload(records)
      setGapWarningOpen(true)
      return
    }
    await submitBulkUpdate(records)
  }

  const handleInlineGapProceed = async () => {
    setGapWarningOpen(false)
    if (!pendingBulkPayload) return
    const records = pendingBulkPayload
    setPendingBulkPayload(null)
    await submitBulkUpdate(records)
  }

  const handleInlineGapCancel = () => {
    setGapWarningOpen(false)
    setPendingBulkPayload(null)
  }

  /** AC3 – Cancel: discard edits, revert rows, exit edit mode. */
  const handleCancelEdit = () => {
    setIsEditAll(false)
    setEditMode(false)
    setEditingData({})
    setFieldErrors({})
    setSelectedItems([])
  }

  // --- DTAA Master: Row-level Edit & Delete handlers ---

  const handleDtaaRowEdit = (uuid: string) => {
    const row = data.find(
      r => (r.associated_uuid || r.global_uuid || r.uuid || r.id) === uuid
    )
    if (!row) return
    setDtaaEditingRow(row)
    setDtaaEditOpen(true)
  }

  const handleDtaaEditSubmit = async (
    formData: Record<string, string | number | null>
  ): Promise<boolean> => {
    if (!dtaaEditingRow) return false
    const uuid = (dtaaEditingRow.associated_uuid ||
      dtaaEditingRow.global_uuid ||
      dtaaEditingRow.uuid ||
      dtaaEditingRow.id) as string
    if (!uuid || uuid.startsWith('row-')) {
      showSnackbar('Cannot update: record UUID not found.', 'error')
      return false
    }
    const result = await updateServiceMasterRecord(
      countryCode,
      apiIdentifierUuid,
      uuid,
      formData,
      activeDataTypeLower,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Record updated successfully', 'success')
      setDtaaEditOpen(false)
      setDtaaEditingRow(null)
      loadData()
      loadAllDataForGapCheck()
      return true
    } else {
      showSnackbar(result.error, 'error')
      return false
    }
  }

  const handleDtaaRowDelete = (uuid: string) => {
    const row = data.find(
      r => (r.associated_uuid || r.global_uuid || r.uuid || r.id) === uuid
    )
    setDtaaDeleteUuid(uuid)
    setDtaaDeleteCountryName((row?.recipient_country as string) || '')
    setDtaaDeleteOpen(true)
  }

  const handleDtaaDeleteConfirm = async () => {
    if (!dtaaDeleteUuid) return
    const result = await deleteDtaaMasterRecord(
      countryCode,
      dtaaDeleteUuid,
      activeDataTypeLower
    )
    if (result.success) {
      showSnackbar('DTAA entry deleted successfully.', 'success')
      setDtaaDeleteOpen(false)
      setDtaaDeleteUuid(null)
      setDtaaDeleteCountryName('')
      loadData()
    } else {
      showSnackbar(result.error, 'error')
    }
  }

  const handleDtaaDeleteCancel = () => {
    setDtaaDeleteOpen(false)
    setDtaaDeleteUuid(null)
    setDtaaDeleteCountryName('')
  }

  // Selection is page-scoped: clear it whenever the page changes.
  const handlePageChange = (page: number) =>
    guardUnsavedChanges(() => {
      setSelectedItems([])
      setCurrentPage(page)
    })
  const handleItemsPerPageChange = (size: number) =>
    guardUnsavedChanges(() => {
      setSelectedItems([])
      setItemsPerPage(size)
      setCurrentPage(1)
    })

  const handleAdd = () => setFormOpen(true)
  const handleFormClose = () => setFormOpen(false)
  const handleFormSubmit = async (
    formData: Record<string, string | number | null>
  ): Promise<boolean> => {
    const result = await createServiceMasterRecord(
      countryCode,
      apiIdentifierUuid,
      formData,
      activeDataTypeLower,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Record created successfully', 'success')
      loadData()
      loadAllDataForGapCheck()
      return true
    } else {
      showSnackbar(result.error, 'error')
      return false
    }
  }

  const handleDownload = async () => {
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar(
        'Country and Entity must be selected for download',
        'warning'
      )
      return
    }
    setDownloading(true)
    const result = await downloadServiceMaster(
      countryCode,
      apiIdentifierUuid,
      searchCOList.length > 0 ? { searchCOList } : undefined,
      activeDataTypeLower,
      { tanApplicable }
    )
    if (result.success) {
      triggerBlobDownload(
        result.data,
        `${activeDataTypeLower}_${countryCode.toLowerCase()}.xlsx`
      )
      showSnackbar('Download started', 'success')
    } else {
      showSnackbar(result.error || 'Download failed', 'error')
    }
    setDownloading(false)
  }

  const openSearchDialog = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget
    guardUnsavedChanges(() => {
      setAnchorEl(target)
      setSearchOpen(true)
    })
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }
  const applySearch = async (searchList: any[]) => {
    // The shared Search component already emits fully-formatted criteria:
    // { fieldName, operator, value } for scalar operators and
    // { fieldName, operator, searchValueList } for the IN_LIST / NOT_IN_LIST /
    // *_PROBABLE_MATCH family (which carry NO `value` key). Pass that straight
    // through — the previous `.filter(i => i.value)` dropped every list-operator
    // criterion (and the rebuild dropped `searchValueList`), so searchCOList was
    // sent empty whenever a list operator was used.
    const mappedList = searchList
    setSearchCOList(mappedList)
    setCurrentPage(1)
    setSearchCriteria(
      searchList.map((i, idx) => ({
        ...i,
        id: idx + 1,
        field: i.field || i.fieldName || '',
        logicalOperator: i.logicalOperator || (idx === 0 ? 'Where' : 'And'),
      }))
    )
    setSearchOpen(false)
    lastFetchedKeyRef.current = ''
    loadData(mappedList)
  }
  const resetSearch = () => {
    setSearchCOList([])
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: visibleColumns[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    loadData([])
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              my: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '130%',
                color: 'var(--text-heading)',
              }}
            >
              {navSubModules.length > 0
                ? navSubModules[activeTab]?.label || 'Service master'
                : 'Service master'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip
                title={downloading ? 'Downloading...' : 'Download Excel'}
              >
                <IconButton
                  size="medium"
                  onClick={handleDownload}
                  disabled={downloading}
                  aria-label="Download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {activeTab === 0 && !editMode && data.length > 0 && (
                <Tooltip
                  title={
                    selectedItems.length === 0 ? 'Edit all (bulk)' : 'Edit'
                  }
                >
                  <IconButton
                    size="medium"
                    onClick={handleEditClick}
                    aria-label="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {activeTab === 0 && editMode && (
                <>
                  <Tooltip title="Save">
                    <IconButton
                      size="medium"
                      onClick={() => handleSaveEdit()}
                      aria-label="Save"
                      sx={{ color: 'var(--forbrand-primary)' }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      size="medium"
                      onClick={handleCancelEdit}
                      aria-label="Cancel"
                      sx={{ color: 'var(--forbrand-primary)' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Search">
                <IconButton
                  size="medium"
                  ref={searchIconRef}
                  onClick={openSearchDialog}
                  aria-label="Search"
                >
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  visibility: activeTab === 0 ? 'visible' : 'hidden',
                  pointerEvents: activeTab === 0 ? 'auto' : 'none',
                }}
              >
                <Box
                  sx={{
                    height: '16px',
                    borderLeft: '1px solid var(--border-subtel)',
                  }}
                />
                <CustomButton onClick={handleAdd}>Add</CustomButton>
              </Box>
            </Box>
          </Box>
          {/* Content-level tabs: render dynamically from navigation API sub-modules */}
          {navSubModules.length > 1 && (
            <Box sx={{ borderBottom: '1px solid var(--border-subtel)' }}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => {
                  guardUnsavedChanges(() => setActiveTab(v))
                }}
                aria-label="Service master content tabs"
                sx={{
                  display: 'flex',
                  alignItems: 'end',
                  backgroundColor: 'var(--bg-layer-1)',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'var(--forbrand-color)',
                    height: '3px',
                  },
                }}
              >
                {navSubModules.map((sub, idx) => (
                  <Tab
                    key={sub.code}
                    label={sub.label}
                    sx={{
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: activeTab === idx ? 600 : 400,
                      minHeight: '32px',
                      padding: '6px 12px',
                      color: 'var(--forbrand-secondary)',
                      '&.Mui-selected': { color: 'var(--forbrand-primary)' },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )}
          {
            <>
              <MTCommonTable
                columns={visibleColumns}
                data={data}
                loading={loading}
                showCheckbox={activeTab === 0}
                showFooter
                selectedItems={selectedItems}
                onSelectItem={handleSelectItem}
                onSelectAll={handleSelectAll}
                editMode={editMode}
                // Edit-all: omit -> every row editable; otherwise only selected rows
                editableRowIds={isEditAll ? undefined : selectedItems}
                editingRowId={null}
                editingData={editingData}
                onFieldChange={handleFieldChange}
                fieldErrors={fieldErrors}
                showSelectedCountInFooter={false}
                rawMetadataFields={rawMetadataFields}
                totalItems={totalItems}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onRowEdit={activeTab === 1 ? handleDtaaRowEdit : undefined}
                // onRowDelete={activeTab === 1 ? handleDtaaRowDelete : undefined}
                emptyMessage={
                  activeTab === 1
                    ? 'No records found'
                    : 'No service master data available'
                }
              />
              <MTServiceMasterForm
                open={formOpen}
                onClose={handleFormClose}
                title={formTitle}
                fields={formFields}
                onSubmit={handleFormSubmit}
                listData={allData.length ? allData : data}
                primaryFields={primaryFields}
              />
              <ServiceMasterSearch
                open={searchOpen}
                anchorEl={anchorEl}
                fields={searchFields as any}
                searchCriteria={searchCriteria}
                setSearchCriteria={setSearchCriteria}
                onClose={closeSearchDialog}
                onApply={applySearch}
                onReset={resetSearch}
                placement="bottom-start"
              />

              {/* DTAA Edit Modal - reuses form fields from metadata */}
              {activeTab === 1 && (
                <MTServiceMasterForm
                  open={dtaaEditOpen}
                  onClose={() => {
                    setDtaaEditOpen(false)
                    setDtaaEditingRow(null)
                  }}
                  title="Edit DTAA master"
                  fields={formFields}
                  onSubmit={handleDtaaEditSubmit}
                  initialValues={dtaaEditingRow ?? undefined}
                  listData={allData.length ? allData : data}
                  primaryFields={primaryFields}
                />
              )}

              {/* Inline edit gap warning */}
              <ConfirmationDialog
                open={gapWarningOpen}
                onClose={handleInlineGapCancel}
                onConfirm={handleInlineGapProceed}
                onCancel={handleInlineGapCancel}
                title="There is a gap between the End Date and Start Date for which there will be no applicability of service and broad head. Do you want to continue?"
                message=" "
                confirmText="Proceed"
                type="warning"
                itemType=""
                showCancelButton={true}
              />
              {/* AC3 – Unsaved changes warning */}
              <ConfirmationDialog
                open={pendingNavAction !== null}
                onClose={handleUnsavedCancel}
                onConfirm={handleUnsavedProceed}
                onCancel={handleUnsavedCancel}
                title={MSG_UNSAVED_CHANGES}
                message=" "
                confirmText="Proceed"
                type="warning"
                itemType=""
                showCancelButton={true}
              />
              {/* DTAA Delete Confirmation Dialog */}
              <ConfirmationDialog
                open={dtaaDeleteOpen}
                onClose={handleDtaaDeleteCancel}
                onConfirm={handleDtaaDeleteConfirm}
                title={`Are you sure you want to delete the DTAA entry for "${dtaaDeleteCountryName || 'this record'}"?`}
                confirmText="Confirm"
                message=""
                type="delete"
                itemType=""
                showCancelButton={true}
                onCancel={handleDtaaDeleteCancel}
              />
            </>
          }
        </>
      )}
    </Box>
  )
}
